// Proposition de plan de tracteurs (docs/routes/repartition/01-architecture.md, lot F, mode TRACTOR_PLAN). Module pur,
// sans API Deno : testé par Vitest (src/features/dispatch/infrastructure/google/tractorPlan.test.ts).
//
// Modèle (mêmes règles que `dispatch_load_v1`, données fournies par `dispatch_optimization_input_v1`) :
//   * charge d'un tracteur = Σ durée interne de ses routes (trajet + service) + Σ transitions entre routes
//     consécutives (fin de A → début de B);
//   * objectif : la plus petite heure de fin du dernier tracteur, puis le plus petit total (moins de route parcourue
//     à vide);
//   * FREE : la route peut changer de tracteur et de rang; ROUTE_LOCKED : elle reste sur son tracteur;
//     POSITION_LOCKED : elle reste sur son tracteur, à son rang;
//   * aucune adresse ne change de route (V1).
// Recherche locale déterministe à partir du brouillon : déplacements d'une route puis échanges de deux routes, la
// meilleure amélioration à chaque tour. Le résultat n'est jamais pire que le brouillon; la base le revérifie.

export type LockMode = 'FREE' | 'ROUTE_LOCKED' | 'POSITION_LOCKED'

export interface PlanLineInput {
  lineId: string
  routes: { routeId: string; lockMode: LockMode }[]
}

export interface TractorPlanInput {
  lines: PlanLineInput[]
  /** Durée interne de chaque route, en secondes. */
  seconds: Record<string, number>
  /** transitions[a][b] : secondes de la fin de a au début de b. Absente : 0 (comme une transition inconnue). */
  transitions: Record<string, Record<string, number>>
}

export interface TractorPlanResult {
  lines: { lineId: string; routeIds: string[] }[]
  before: { lastFinishSeconds: number; totalSeconds: number }
  after: { lastFinishSeconds: number; totalSeconds: number }
  iterations: number
  improved: boolean
}

export const maxIterations = 200

type Plan = string[][]

interface Score {
  last: number
  total: number
}

function better(a: Score, b: Score): boolean {
  return a.last < b.last || (a.last === b.last && a.total < b.total)
}

export function lineSeconds(routeIds: readonly string[], input: TractorPlanInput): number {
  let total = 0
  routeIds.forEach((routeId, index) => {
    total += input.seconds[routeId] ?? 0
    const previous = index > 0 ? routeIds[index - 1] : undefined
    if (previous !== undefined) total += input.transitions[previous]?.[routeId] ?? 0
  })
  return total
}

function score(plan: Plan, input: TractorPlanInput): Score {
  let last = 0
  let total = 0
  for (const line of plan) {
    const value = lineSeconds(line, input)
    last = Math.max(last, value)
    total += value
  }
  return { last, total }
}

export function optimizeTractorPlan(input: TractorPlanInput): TractorPlanResult {
  const ids = input.lines.flatMap((line) => line.routes.map((route) => route.routeId))
  if (new Set(ids).size !== ids.length) throw new Error('INVALID_INPUT')
  const lineOf = new Map<string, number>()
  const lock = new Map<string, LockMode>()
  const fixedRank = new Map<string, number>()
  input.lines.forEach((line, lineIndex) => {
    line.routes.forEach((route, rank) => {
      lineOf.set(route.routeId, lineIndex)
      lock.set(route.routeId, route.lockMode)
      if (route.lockMode === 'POSITION_LOCKED') fixedRank.set(route.routeId, rank)
    })
  })

  /** Verrous respectés : tracteur d'origine pour ROUTE_LOCKED et POSITION_LOCKED, rang d'origine pour le second. */
  const valid = (plan: Plan, touched: readonly number[]) =>
    touched.every((lineIndex) =>
      (plan[lineIndex] ?? []).every((routeId, rank) => {
        const mode = lock.get(routeId)
        if (mode !== 'FREE' && lineOf.get(routeId) !== lineIndex) return false
        return mode !== 'POSITION_LOCKED' || fixedRank.get(routeId) === rank
      }),
    )

  let plan: Plan = input.lines.map((line) => line.routes.map((route) => route.routeId))
  const start = score(plan, input)
  let current = start
  let iterations = 0
  for (; iterations < maxIterations; iterations += 1) {
    let best: { plan: Plan; score: Score } | null = null
    const consider = (candidate: Plan, touched: number[]) => {
      if (!valid(candidate, touched)) return
      const value = score(candidate, input)
      if (better(value, best?.score ?? current)) best = { plan: candidate, score: value }
    }
    // Déplacer une route (vers une autre ligne ou un autre rang de sa ligne).
    plan.forEach((line, from) => {
      line.forEach((routeId, rank) => {
        if (lock.get(routeId) === 'POSITION_LOCKED') return
        const without = line.filter((_, index) => index !== rank)
        plan.forEach((target, to) => {
          if (lock.get(routeId) === 'ROUTE_LOCKED' && to !== lineOf.get(routeId)) return
          const base = to === from ? without : target
          for (let position = 0; position <= base.length; position += 1) {
            if (to === from && position === rank) continue
            const next = plan.map((items) => [...items])
            next[from] = [...without]
            const inserted = [...base.slice(0, position), routeId, ...base.slice(position)]
            next[to] = inserted
            consider(next, to === from ? [from] : [from, to])
          }
        })
      })
    })
    // Échanger deux routes de lignes différentes.
    plan.forEach((lineA, a) => {
      plan.forEach((lineB, b) => {
        if (b <= a) return
        lineA.forEach((routeA, rankA) => {
          if (lock.get(routeA) !== 'FREE') return
          lineB.forEach((routeB, rankB) => {
            if (lock.get(routeB) !== 'FREE') return
            const next = plan.map((items) => [...items])
            next[a] = lineA.map((id, index) => (index === rankA ? routeB : id))
            next[b] = lineB.map((id, index) => (index === rankB ? routeA : id))
            consider(next, [a, b])
          })
        })
      })
    })
    const found = best as { plan: Plan; score: Score } | null
    if (!found) break
    plan = found.plan
    current = found.score
  }
  return {
    lines: input.lines.map((line, index) => ({ lineId: line.lineId, routeIds: plan[index] ?? [] })),
    before: { lastFinishSeconds: start.last, totalSeconds: start.total },
    after: { lastFinishSeconds: current.last, totalSeconds: current.total },
    iterations,
    improved: better(current, start),
  }
}
